import { defineComponent as _defineComponent } from 'vue'
import moment from 'moment';
import { useApplicationStore } from '@/store/applicationStore';
import { useGatComponentsStore } from '@/store/gatComponentsStore';
import { ref, onActivated, onMounted, computed, nextTick } from 'vue';
import { useGatInput } from './useGatInput';
import { useGatDateEditModelValue } from '../composables/useGatDateEditModelValue';
import GatDateEditFunctions from '../common/GatDateEditFunctions';

interface Props {
  value: string | null;
  dense?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  errorMessages?: string | string[];
  hint?: string;
  label?: string;
  inlineEdit?: boolean;
  readonly?: boolean;
  required?: boolean;
  markOptionalInputs?: boolean;
  futureDate?: boolean;
  showWeekDay?: 'none' | 'auto' | 'dd' | 'ddd' | 'dddd';
  validatorRules?: any[];
  autocomplete?: string;
  dataFormType: string;
  dataLpignore: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GatDateEdit',
  props: {
    value: { default: null },
    dense: { type: Boolean },
    clearable: { type: Boolean },
    disabled: { type: Boolean },
    errorMessages: null,
    hint: null,
    label: null,
    inlineEdit: { type: Boolean },
    readonly: { type: Boolean },
    required: { type: Boolean },
    markOptionalInputs: { type: Boolean },
    futureDate: { type: Boolean },
    showWeekDay: { default: 'none' },
    validatorRules: null,
    autocomplete: { default: 'off' },
    dataFormType: { default: 'other' },
    dataLpignore: { default: 'true' }
  },
  emits: ["OnErrorState", "input", "blur"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'OnErrorState', payload: any): void;
  (e: 'input', value: string | null): void;
  (e: 'blur', event: any): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: string | null, dense?: boolean, clearable?: boolean, disabled?: boolean, errorMessages?: string | string[], hint?: string, label?: string, inlineEdit?: boolean, readonly?: boolean, required?: boolean, markOptionalInputs?: boolean, futureDate?: boolean, showWeekDay: 'none' | 'auto' | 'dd' | 'ddd' | 'dddd', validatorRules?: any[], autocomplete: string, dataFormType: string, dataLpignore: string };



const { dateEditValue, setDate } = useGatDateEditModelValue(props);



const field = ref<null | any>(null);

const applicationStore = useApplicationStore();
const gatComponentsStore = useGatComponentsStore();
const { getLabel, getMessages } = useGatInput(props, emit);

const elWidth = ref<null | number>(null);
const showDatePicker = ref(false);
const pickerDate = ref<string | null>(null);

const displayFormat = computed(() =>
  applicationStore.settings.dateFormat ? applicationStore.settings.dateFormat : 'DD.MM.YYYY'
);

function textToValidDateTextOrNull(text: string | null, inFormat: string): string | null {
  if (text) {
    return GatDateEditFunctions.parseTextToDate(text, inFormat, props.futureDate).parsedDate?.toISODate() || null;
  }
  return null;
}

function elementIsReadOnlyByForm() {
  // Ikke funnet noen god måte å hente readonly status fra v-form.  Sjekker readonly attributtet på input elementet isteden.
  try {
    const readonlyInput = field.value.$el.querySelector('input[readonly]');
    if (readonlyInput) {
      return true;
    }
  } catch (error) {
    // silent
  }

  return false;
}

const firstDayofYear = computed(() => {
  let result = 4;
  if (gatComponentsStore.calendarSettings.weekNumberInCalendar == 'us') {
    result = 0;
  }
  return result;
});
const firstDayOfWeek = computed(() => {
  let result = 1;
  if (gatComponentsStore.calendarSettings.weekStartDay == 'sunday') {
    result = 0;
  } else if (gatComponentsStore.calendarSettings.weekStartDay == 'saturday') {
    result = 6;
  }
  return result;
});
// const getHint = computed(() => {
//   let result = props.hint;
//   if (!result) {
//     result = 'F2=todays date, F9 = show calendar';
//   }
//   return result;
// });

const isOutlined = computed(() => !props.inlineEdit);

const isFilled = computed(() => props.inlineEdit);

function isValidDate(text: string | null, format = displayFormat.value) {
  if (text) {
    return GatDateEditFunctions.parseTextToDate(text, format).isValid;
  }
  return false;
}

const textValueIsValidDateOrEmpty = computed(() => {
  if (dateEditValue.value === '' || dateEditValue.value === null) {
    return true;
  }
  return isValidDate(dateEditValue.value, displayFormat.value);
});

const rules = computed(() => {
  let result: any[] = [];
  // custom rules
  if (props.validatorRules) {
    result = result.concat(props.validatorRules);
  }
  // required rule
  if (props.required) {
    result.push((value: any) => {
      if (value) {
        return true;
      }
      return 'required';
    });
  }
  // invlaid date rule
  result.push((value: string | null) => {
    if (value) {
      const parserResult = GatDateEditFunctions.parseTextToDate(value, displayFormat.value);
      if (!parserResult.isValid) {
        return parserResult.invalidReason ?? `Invalid date '${dateEditValue.value}'`;
      }
    }
    return true;
  });

  return result;
});

function onClearCliked() {
  emit('input', null);
}

function pickDateFromCalendar() {
  if (isValidDate(props.value, 'YYYY-MM-DD') && typeof props.value === 'string') {
    pickerDate.value = props.value;
  } else {
    pickerDate.value = moment(Date.now()).format('YYYY-MM-DD');
  }
  showDatePicker.value = true;
}

// on blur, focus leaving edit
function onBlur(eventParams?: Event) {
  // find correct date from given text value
  if (textValueIsValidDateOrEmpty.value) {
    const date = textToValidDateTextOrNull(dateEditValue.value, displayFormat.value);
    // if found date is different from the value property, emit input event
    if (date !== props.value) {
      emit('input', date);
    } // if date is the same as property, but text value is not the same as date, set text value to date
    else if (date !== dateEditValue.value && !(date == null && dateEditValue.value === '')) {
      // eslint-disable-next-line no-param-reassign
      dateEditValue.value = moment(date).format(displayFormat.value);
    }
  }
  // Emitted when the input is blurred
  emit('blur', eventParams);
}

function setDateFromPicker() {
  const isoFormatted = pickerDate.value;
  setDate(isoFormatted);
  emit('input', isoFormatted);
  showDatePicker.value = false;
  onBlur();
}

const calendarBtnIsVisible = computed(() => {
  let isVisible = elWidth.value && elWidth.value > 125;
  isVisible = isVisible && !props.readonly && !elementIsReadOnlyByForm();
  return isVisible;
});

function getSuffixFormat() {
  const { showWeekDay } = props;
  let calendarBtnWidth = 0;
  if (calendarBtnIsVisible.value) {
    calendarBtnWidth = 12;
  }
  if (showWeekDay === 'auto' && elWidth.value) {
    if (elWidth.value > 200 + calendarBtnWidth) {
      return 'dddd';
    }
    if (elWidth.value > 150 + calendarBtnWidth) {
      return 'ddd';
    }
    if (elWidth.value > 140 + calendarBtnWidth) {
      return 'dd';
    }
    return 'none';
  }
  if (showWeekDay === undefined || showWeekDay === 'auto') {
    return 'none';
  }
  return showWeekDay;
}

const dataFormat = 'YYYY-MM-DD';

function getSuffix(): string | undefined {
  if (isValidDate(props.value, dataFormat) && getSuffixFormat() !== 'none') {
    const date = moment(props.value, dataFormat);
    return date.format(getSuffixFormat());
  }
  return undefined;
}

function updateElementWidth() {
  if (field.value) {
    const fld = field.value as any;
    elWidth.value = fld.$el.getBoundingClientRect().width;
  } else {
    elWidth.value = 0;
  }
}

async function validate() {
  await nextTick();
  if (field.value) {
    const result = await field.value.validate();
    return result;
  }
  return [];
}

expose({
  validate,
});

onActivated(() => {
  updateElementWidth();
});

onMounted(() => {
  nextTick(() => {
    updateElementWidth();
  });
});

return { __sfc: true,props, dateEditValue, setDate, emit, field, applicationStore, gatComponentsStore, getLabel, getMessages, elWidth, showDatePicker, pickerDate, displayFormat, textToValidDateTextOrNull, elementIsReadOnlyByForm, firstDayofYear, firstDayOfWeek, isOutlined, isFilled, isValidDate, textValueIsValidDateOrEmpty, rules, onClearCliked, pickDateFromCalendar, onBlur, setDateFromPicker, calendarBtnIsVisible, getSuffixFormat, dataFormat, getSuffix, updateElementWidth, validate }
}

})